import { useState, useCallback } from 'react'

/**
 * Toast types
 */
export type ToastType = 'success' | 'error' | 'warning' | 'info'

/**
 * Toast options
 */
export interface ToastOptions {
  /** Toast type (default: 'info') */
  type?: ToastType
  /** Duration in milliseconds (default: 3000) */
  duration?: number
  /** Toast title */
  title?: string
  /** Toast description */
  description?: string
  /** Action button label */
  action?: {
    label: string
    onClick: () => void
  }
}

/**
 * Toast item
 */
export interface Toast {
  id: string
  type: ToastType
  title?: string
  description?: string
  action?: {
    label: string
    onClick: () => void
  }
  createdAt: Date
}

/**
 * Toast state
 */
interface ToastState {
  toasts: Toast[]
}

// Global toast state (singleton)
let globalToastState: ToastState = {
  toasts: []
}

// Global listeners
const listeners = new Set<(state: ToastState) => void>()

/**
 * Notify all listeners of state changes
 */
function notifyListeners() {
  listeners.forEach(listener => listener(globalToastState))
}

/**
 * Generate unique ID
 */
function generateId(): string {
  return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Add a toast to global state
 */
function addToast(toast: Toast, duration: number) {
  globalToastState = {
    ...globalToastState,
    toasts: [...globalToastState.toasts, toast]
  }
  notifyListeners()

  // Auto-dismiss after duration
  if (duration > 0) {
    setTimeout(() => {
      removeToast(toast.id)
    }, duration)
  }
}

/**
 * Remove a toast from global state
 */
function removeToast(id: string) {
  globalToastState = {
    ...globalToastState,
    toasts: globalToastState.toasts.filter(t => t.id !== id)
  }
  notifyListeners()
}

/**
 * Hook for toast notification state management
 * Provides a simple interface for showing success/error/info/warning toasts
 *
 * Usage:
 * const { toast, success, error, info, warning, dismiss } = useToast()
 *
 * toast('Hello world', { type: 'success' })
 * success('Operation successful')
 * error('Something went wrong')
 */
export function useToast() {
  const [state, setState] = useState<ToastState>(globalToastState)

  // Subscribe to global state changes
  useState(() => {
    listeners.add(setState)
    return () => {
      listeners.delete(setState)
    }
  })

  /**
   * Show a toast notification
   * Can be called with:
   * - toast(message, options) - traditional way
   * - toast({ title, description, variant }) - shadcn/ui way
   */
  const toast = useCallback((messageOrOptions: string | ToastOptions & { variant?: 'default' | 'destructive' }, options?: ToastOptions) => {
    let finalOptions: ToastOptions

    if (typeof messageOrOptions === 'string') {
      // Traditional: toast('message', { ...options })
      finalOptions = { ...options, title: options?.title || messageOrOptions }
    } else {
      // Modern: toast({ title, description, variant })
      const { variant, title, description, ...rest } = messageOrOptions
      finalOptions = {
        ...rest,
        title,
        description,
        type: variant === 'destructive' ? 'error' : (rest.type || 'info')
      }
    }

    const {
      type = 'info',
      duration = 3000,
      title,
      description,
      action
    } = finalOptions

    const newToast: Toast = {
      id: generateId(),
      type,
      title,
      description,
      action,
      createdAt: new Date()
    }

    addToast(newToast, duration)
    return newToast.id
  }, [])

  /**
   * Show a success toast
   */
  const success = useCallback((message: string, options?: Omit<ToastOptions, 'type'>) => {
    return toast(message, { ...options, type: 'success' })
  }, [toast])

  /**
   * Show an error toast
   */
  const error = useCallback((message: string, options?: Omit<ToastOptions, 'type'>) => {
    return toast(message, { ...options, type: 'error', duration: options?.duration || 5000 })
  }, [toast])

  /**
   * Show an info toast
   */
  const info = useCallback((message: string, options?: Omit<ToastOptions, 'type'>) => {
    return toast(message, { ...options, type: 'info' })
  }, [toast])

  /**
   * Show a warning toast
   */
  const warning = useCallback((message: string, options?: Omit<ToastOptions, 'type'>) => {
    return toast(message, { ...options, type: 'warning', duration: options?.duration || 4000 })
  }, [toast])

  /**
   * Dismiss a toast by ID
   */
  const dismiss = useCallback((id: string) => {
    removeToast(id)
  }, [])

  /**
   * Dismiss all toasts
   */
  const dismissAll = useCallback(() => {
    globalToastState = { toasts: [] }
    notifyListeners()
  }, [])

  return {
    toast,
    success,
    error,
    info,
    warning,
    dismiss,
    dismissAll,
    toasts: state.toasts
  }
}
